import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

const CmmiServices: React.FC = () => {
  const cmmiLevels = [
    {
      level: "Capability Level 1",
      description: "Initial - Process unpredictable, poorly controlled and reactive",
    },
    {
      level: "Capability Level 2", 
      description: "Managed - Process characterized for projects and is often reactive",
    },
    {
      level: "Capability Level 3",
      description: "Defined - Process characterized for the organization and is proactive",
    },
    {
      level: "Capability Level 4",
      description: "Quantitatively Managed - Process measured and controlled",
    },
    {
      level: "Capability Level 5",
      description: "Optimizing - Focus on process improvement",
    }
  ];

  const cmmiServices = [
    {
      title: "CMMI Appraisal Services",
      description: "Our expert team conducts thorough CMMI appraisals to help you determine your organization's current capability level.",
      benefits: [
        "Objective assessment of your processes",
        "Detailed findings and recommendations",
        "Identification of process strengths and weaknesses",
        "Clear pathway for improvement"
      ],
      path: "/services/cmmi/appraisal"
    },
    {
      title: "CMMI Consultancy Services",
      description: "Our consultancy services help you implement CMMI practices and improve your organization's process maturity.",
      benefits: [
        "Customized implementation plans",
        "Process documentation and improvement",
        "Training and mentoring",
        "Preparation for formal appraisals"
      ],
      path: "/services/cmmi/consultancy"
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom textAlign="center">
              CMMI Services
            </Typography>
            <Typography variant="h6" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto', opacity: 0.9 }}>
              Comprehensive CMMI solutions to enhance your organizational processes
            </Typography>
          </motion.div>
        </Container>
        
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: 60,
          right: 120,
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 40,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-24px' }}>
          <div style={{ width: '50%', padding: '24px' }}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom 
                fontWeight="bold"
                color="#0c2340"
              >
                What is CMMI?
              </Typography>
              <Typography paragraph>
                The Capability Maturity Model Integration (CMMI) is a process level improvement training 
                and appraisal program administered by the CMMI Institute, a subsidiary of ISACA. 
                CMMI is a proven set of global best practices that drives business performance through building 
                and benchmarking key capabilities.
              </Typography>
              <Typography paragraph>
                CMMI helps organizations streamline process improvement and encourage productive, efficient behaviors 
                that decrease risks in software, product, and service development.
              </Typography>
            </motion.div>
          </div>
          <div style={{ width: '50%', padding: '24px' }}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <Box 
                component="img"
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80"
                alt="CMMI Process Improvement"
                sx={{
                  width: '100%',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </div>
        </div>
      </Container>

      {/* CMMI Levels Section */}
      <Box sx={{ bgcolor: '#f5f7fa', py: 10 }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Typography 
              variant="h4" 
              component="h2" 
              gutterBottom 
              fontWeight="bold"
              color="#0c2340"
              textAlign="center"
              sx={{ mb: 6 }}
            >
              CMMI Capability Levels
            </Typography>
          </motion.div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-12px' }}>
            {cmmiLevels.map((level, index) => (
              <div key={index} style={{ width: '33.33%', padding: '12px' }}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 5px 20px rgba(0,0,0,0.05)',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-10px)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      bgcolor: '#1976d2',
                    }} />
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight="bold"
                      color="primary"
                    >
                      {level.level}
                    </Typography>
                    <Typography color="text.secondary">
                      {level.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </div>
            ))}
          </div>
        </Container>
      </Box>

      {/* Our CMMI Services Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            fontWeight="bold"
            color="#0c2340"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Our CMMI Services
          </Typography>
        </motion.div>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-20px' }}>
          {cmmiServices.map((service, index) => (
            <div key={index} style={{ width: '50%', padding: '20px' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    height: '100%',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    backgroundColor: '#1976d215',
                    color: '#1976d2',
                    mb: 3,
                  }}>
                    <DocumentScannerIcon sx={{ fontSize: 40 }} />
                  </Box>
                  
                  <Typography variant="h5" component="h3" fontWeight="bold" gutterBottom color="#0c2340">
                    {service.title}
                  </Typography>
                  
                  <Typography paragraph color="text.secondary" sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <Typography variant="h6" gutterBottom fontWeight="medium">
                    Key Benefits:
                  </Typography>
                  
                  <List sx={{ mb: 3 }}>
                    {service.benefits.map((benefit, idx) => (
                      <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleOutlineIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={benefit} />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Box sx={{ mt: 'auto' }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      component={Link}
                      to={service.path}
                      sx={{
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: '#1976d215',
                        },
                      }}
                    >
                      Learn More
                    </Button>
                  </Box>
                </Paper>
              </motion.div>
            </div>
          ))}
        </div>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        backgroundColor: '#f5f7fa', 
        py: 12,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                position: 'relative'
              }}
            >
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Ready to Improve Your Processes?
              </Typography>
              <Typography 
                variant="body1" 
                paragraph 
                sx={{ 
                  maxWidth: '700px', 
                  mx: 'auto', 
                  mb: 4, 
                  opacity: 0.9 
                }}
              >
                Our CMMI experts are ready to help you assess and improve your organizational processes. 
                Contact us today for a free consultation.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/contact?service=cmmi"
                  sx={{
                    bgcolor: 'white',
                    color: '#0c2340',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/services"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)',
                    },
                    px: 4,
                    py: 1.5,
                    borderRadius: '8px'
                  }}
                >
                  View All Services
                </Button>
              </Box>

              {/* Decorative circles */}
              <Box sx={{
                position: 'absolute',
                top: -30,
                left: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
              <Box sx={{
                position: 'absolute',
                bottom: -40,
                right: -40,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.03)',
                zIndex: 0
              }} />
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default CmmiServices; 